const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const TOKEN_KEY = 'srt.auth.token';
const USER_KEY  = 'srt.auth.user';

export const auth = {
  get token()   { try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; } },
  get user()    {
    try { const v = localStorage.getItem(USER_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
  },
  set(token, user) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {}
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    } catch {}
  },
};

// Headers
function authHeaders(extra) {
  const h = { ...(extra || {}) };
  const t = auth.token;
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

// Unauthorized responses → clear session and bubble up a structured error so
// the app can drop the user back to /login.
async function handleResponse(res) {
  // multipart upload routes may return non-JSON on edge cases; guard against that.
  let data = null;
  try { data = await res.json(); } catch { data = null; }

  if (res.status === 401) {
    // A 401 on an *authenticated* request (we had a token) means the session
    // expired / was rejected → clear it and bounce to /login. A 401 with no
    // token is the login request itself failing → surface the server's actual
    // message ("Invalid email or password") instead of a misleading expiry.
    if (auth.token) {
      auth.clear();
      const err = new Error('Session expired. Please log in again.');
      err.status = 401;
      throw err;
    }
    const err = new Error((data && data.error) || 'Invalid email or password');
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

async function request(method, url, body) {
  const options = {
    method,
    headers: authHeaders({ 'Content-Type': 'application/json' }),
  };
  if (body) options.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${url}`, options);
  return handleResponse(res);
}

async function upload(method, url, formData) {
  const res = await fetch(`${BASE}${url}`, {
    method,
    body: formData,
    headers: authHeaders(),
  });
  return handleResponse(res);
}

export { BASE, authHeaders };

export const api = {
  auth: {
    login: async (body) => {
      const data = await request('POST', '/auth/login', body);
      if (data && data.token && data.user) auth.set(data.token, data.user);
      return data;
    },
    logout: () => auth.clear(),
    current: () => auth.user,
    forgotPassword: (email) => request('POST', '/auth/forgot-password', { email }),
    resetPassword: (token, password) => request('POST', '/auth/reset-password', { token, password }),
    setup2FA:       (tempToken)       => request('POST', '/auth/2fa/setup',         { tempToken }),
    verifySetup2FA: async (tempToken, code) => {
      const data = await request('POST', '/auth/2fa/verify-setup', { tempToken, code });
      if (data?.token && data?.user) auth.set(data.token, data.user);
      return data;
    },
    verify2FA: async (tempToken, code) => {
      const data = await request('POST', '/auth/2fa/verify', { tempToken, code });
      if (data?.token && data?.user) auth.set(data.token, data.user);
      return data;
    },
    disable2FA:     (password, code)  => request('POST', '/auth/2fa/disable',        { password, code }),
  },
  activity: {
    list: (params) =>
      request("GET", `/activity?${new URLSearchParams(params)}`),
  },
  affiliates: {
    list:           ()           => request('GET',    '/affiliates'),
    detail:         (id)         => request('GET',    `/affiliates/${id}/detail`),
    my:             (affId)      => request('GET',    `/affiliates/my/${affId}`),
    parentDashboard:(affId)      => request('GET',    `/affiliates/my/${affId}/parent-dashboard`),
    create:         (body)       => request('POST',   '/affiliates', body),
    update:         (id, body)   => request('PATCH',  `/affiliates/${id}`, body),
    updateStatus:   (id, status) => request('PATCH',  `/affiliates/${id}`, { status }),
    delete:         (id)         => request('DELETE', `/affiliates/${id}`),
    purge:          ()           => request('POST',   '/affiliates/purge', {}),
    assignChannels: (id, ids)    => request('POST',   `/affiliates/${id}/channels`, { channel_ids: ids }),
    removeChannel:  (id, chId)   => request('DELETE', `/affiliates/${id}/channels/${chId}`),
    dashboard: () => request('GET', '/affiliates/dashboard'),
  },
  channels: {
    list:         ()           => request('GET',   '/channels'),
    create:       (body)       => request('POST',  '/channels', body),
    update:       (id, body)   => request('PATCH', `/channels/${id}`, body),
    updateStatus: (id, status) => request('PATCH', `/channels/${id}`, { status }),
  },
  devices: {
    list:            (params)   => request('GET',    `/devices?${new URLSearchParams(params)}`),
    summary:         ()         => request('GET',    '/devices/summary'),
    get:             (id)       => request('GET',    `/devices/${id}`),
    create:          (body)     => request('POST',   '/devices', body),
    update:          (id, body) => request('PATCH',  `/devices/${id}`, body),
    patch:           (id, body) => request('PATCH',  `/devices/${id}`, body),
    updateStatus:    (id, status) => request('PATCH', `/devices/${id}`, { status }),
    assignChannel:   (id, body)    => request('POST',   `/devices/${id}/channel`, body),
    unassignChannel: (id, chId)    => request('DELETE', `/devices/${id}/channel/${chId}`),
    unlinkAffiliate: (id)       => request('DELETE', `/devices/${id}/affiliate`),
    takenChannels:   ()              => request('GET', '/devices/taken-channels'),
    channelLimits:   (affiliateId)   => request('GET', `/devices/channel-limits${affiliateId ? `?affiliate_id=${affiliateId}` : ''}`),
    models:          ()              => request('GET', '/devices/models'),
    saveConfig:      (id, body) => request('PATCH',  `/devices/${id}/config`, body),
    block:           (id, reason) => request('POST', `/devices/${id}/block`, { reason }),
    unblock:         (id)       => request('POST',   `/devices/${id}/unblock`),
    activate:        (id)       => request('POST',   `/devices/${id}/activate`),
    deactivate:      (id)       => request('POST',   `/devices/${id}/deactivate`),
  },
  logs: {
    list: (params) => request('GET', `/logs?${new URLSearchParams(params)}`),
  },
  ota: {
    list:   ()    => request('GET',    '/ota'),
    delete: (id)  => request('DELETE', `/ota/${id}`),
    create: ({ file, version, description, target_level, force_update, target_ids, target_model, target_image }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', version);
      fd.append('description', description);
      fd.append('target_level', target_level);
      // '' = any model. The server resolves this to a canonical family or 400s.
      fd.append('target_model', target_model || '');
      // Image bundles only — which of the box's images this replaces. The
      // server derives bundle shape from the file itself and requires this for
      // an image tarball, rejecting it for a script zip.
      fd.append('target_image', target_image || '');
      fd.append('force_update', String(!!force_update));
      fd.append('target_ids', JSON.stringify(target_ids || []));
      return upload('POST', '/ota', fd);
    },
  },
  profile: {
    updateName: (name) => request('PATCH', '/auth/profile/name', { name }),
    changePassword: (body) => request('PATCH', '/auth/profile/password', body),
  },
  users: {
    list:   ()           => request('GET',    '/users'),
    create: (body)        => request('POST',   '/users', body),
    update: (id, body)    => request('PATCH',  `/users/${id}`, body),
    delete: (id)          => request('DELETE', `/users/${id}`),
  },
};

// Returns true when the error was an auth failure and has been handled, so
// callers can `if (handleAuthFailure(e)) return;` and skip their own error UI.
// Without a return value every one of those guards is a no-op and pages render
// a misleading "Failed to load data" instead of bouncing to login.
export function handleAuthFailure(err, onLogout) {
  if (err?.status === 401 || err?.message?.includes('Session expired')) {
    auth.clear();
    // auth.clear() only touches localStorage; App holds the user in useState,
    // so without a reload the logged-in shell keeps rendering. Reloading makes
    // App re-read storage and land on Login.
    if (typeof onLogout === 'function') onLogout();
    else try { window.location.reload(); } catch {}
    return true;
  }
  return false;
}


//platformauth added
// const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// const TOKEN_KEY = 'srt.auth.token';
// const USER_KEY  = 'srt.auth.user';

// export const auth = {
//   get token()   { try { return sessionStorage.getItem(TOKEN_KEY) || null; } catch { return null; } },
//   get user()    {
//     try { const v = sessionStorage.getItem(USER_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
//   },
//   set(token, user) {
//     try {
//       sessionStorage.setItem(TOKEN_KEY, token);
//       sessionStorage.setItem(USER_KEY, JSON.stringify(user));
//     } catch {}
//   },
//   clear() {
//     try {
//       sessionStorage.removeItem(TOKEN_KEY);
//       sessionStorage.removeItem(USER_KEY);
//     } catch {}
//   },
// };

// // Headers
// function authHeaders(extra) {
//   const h = { ...(extra || {}) };
//   const t = auth.token;
//   if (t) h.Authorization = `Bearer ${t}`;
//   return h;
// }

// // Unauthorized responses → clear session and bubble up a structured error so
// // the app can drop the user back to /login.
// async function handleResponse(res) {
//   // multipart upload routes may return non-JSON on edge cases; guard against that.
//   let data = null;
//   try { data = await res.json(); } catch { data = null; }

//   if (res.status === 401) {
//     // A 401 on an *authenticated* request (we had a token) means the session
//     // expired / was rejected → clear it and bounce to /login. A 401 with no
//     // token is the login request itself failing → surface the server's actual
//     // message ("Invalid email or password") instead of a misleading expiry.
//     if (auth.token) {
//       auth.clear();
//       const err = new Error('Session expired. Please log in again.');
//       err.status = 401;
//       throw err;
//     }
//     const err = new Error((data && data.error) || 'Invalid email or password');
//     err.status = 401;
//     throw err;
//   }

//   if (!res.ok) {
//     const err = new Error((data && data.error) || `Request failed: ${res.status}`);
//     err.status = res.status;
//     throw err;
//   }
//   return data;
// }

// async function request(method, url, body) {
//   const options = {
//     method,
//     headers: authHeaders({ 'Content-Type': 'application/json' }),
//   };
//   if (body) options.body = JSON.stringify(body);
//   const res = await fetch(`${BASE}${url}`, options);
//   return handleResponse(res);
// }

// async function upload(method, url, formData) {
//   const res = await fetch(`${BASE}${url}`, {
//     method,
//     body: formData,
//     headers: authHeaders(),
//   });
//   return handleResponse(res);
// }

// export { BASE, authHeaders };

// export const api = {
//   auth: {
//     login: async (body) => {
//       const data = await request('POST', '/auth/login', body);
//       if (data && data.token && data.user) auth.set(data.token, data.user);
//       return data;
//     },
//     logout: () => auth.clear(),
//     current: () => auth.user,
//     forgotPassword: (email) => request('POST', '/auth/forgot-password', { email }),
//     resetPassword: (token, password) => request('POST', '/auth/reset-password', { token, password }),
//     setup2FA:       (tempToken)       => request('POST', '/auth/2fa/setup',         { tempToken }),
//     verifySetup2FA: async (tempToken, code) => {
//       const data = await request('POST', '/auth/2fa/verify-setup', { tempToken, code });
//       if (data?.token && data?.user) auth.set(data.token, data.user);
//       return data;
//     },
//     verify2FA: async (tempToken, code) => {
//       const data = await request('POST', '/auth/2fa/verify', { tempToken, code });
//       if (data?.token && data?.user) auth.set(data.token, data.user);
//       return data;
//     },
//     disable2FA:     (password, code)  => request('POST', '/auth/2fa/disable',        { password, code }),
//   },
//   activity: {
//     list: (params) =>
//       request("GET", `/activity?${new URLSearchParams(params)}`),
//   },
//   affiliates: {
//     list:           ()           => request('GET',    '/affiliates'),
//     detail:         (id)         => request('GET',    `/affiliates/${id}/detail`),
//     my:             (affId)      => request('GET',    `/affiliates/my/${affId}`),
//     parentDashboard:(affId)      => request('GET',    `/affiliates/my/${affId}/parent-dashboard`),
//     create:         (body)       => request('POST',   '/affiliates', body),
//     update:         (id, body)   => request('PATCH',  `/affiliates/${id}`, body),
//     updateStatus:   (id, status) => request('PATCH',  `/affiliates/${id}`, { status }),
//     delete:         (id)         => request('DELETE', `/affiliates/${id}`),
//     purge:          ()           => request('POST',   '/affiliates/purge', {}),
//     assignChannels: (id, ids)    => request('POST',   `/affiliates/${id}/channels`, { channel_ids: ids }),
//     removeChannel:  (id, chId)   => request('DELETE', `/affiliates/${id}/channels/${chId}`),
//     dashboard: () => request('GET', '/affiliates/dashboard'),
//   },
//   channels: {
//     list:         ()           => request('GET',   '/channels'),
//     create:       (body)       => request('POST',  '/channels', body),
//     update:       (id, body)   => request('PATCH', `/channels/${id}`, body),
//     updateStatus: (id, status) => request('PATCH', `/channels/${id}`, { status }),
//   },
//   devices: {
//     list:            (params)   => request('GET',    `/devices?${new URLSearchParams(params)}`),
//     summary:         ()         => request('GET',    '/devices/summary'),
//     get:             (id)       => request('GET',    `/devices/${id}`),
//     create:          (body)     => request('POST',   '/devices', body),
//     update:          (id, body) => request('PATCH',  `/devices/${id}`, body),
//     patch:           (id, body) => request('PATCH',  `/devices/${id}`, body),
//     updateStatus:    (id, status) => request('PATCH', `/devices/${id}`, { status }),
//     assignChannel:   (id, body)    => request('POST',   `/devices/${id}/channel`, body),
//     unassignChannel: (id, chId)    => request('DELETE', `/devices/${id}/channel/${chId}`),
//     unlinkAffiliate: (id)       => request('DELETE', `/devices/${id}/affiliate`),
//     takenChannels:   ()              => request('GET', '/devices/taken-channels'),
//     channelLimits:   (affiliateId)   => request('GET', `/devices/channel-limits${affiliateId ? `?affiliate_id=${affiliateId}` : ''}`),
//     models:          ()              => request('GET', '/devices/models'),
//     saveConfig:      (id, body) => request('PATCH',  `/devices/${id}/config`, body),
//     block:           (id, reason) => request('POST', `/devices/${id}/block`, { reason }),
//     unblock:         (id)       => request('POST',   `/devices/${id}/unblock`),
//     activate:        (id)       => request('POST',   `/devices/${id}/activate`),
//     deactivate:      (id)       => request('POST',   `/devices/${id}/deactivate`),
//   },
//   logs: {
//     list: (params) => request('GET', `/logs?${new URLSearchParams(params)}`),
//   },
//   ota: {
//     list:   ()    => request('GET',    '/ota'),
//     delete: (id)  => request('DELETE', `/ota/${id}`),
//     create: ({ file, version, description, target_level, force_update, target_ids }) => {
//       const fd = new FormData();
//       fd.append('file', file);
//       fd.append('version', version);
//       fd.append('description', description);
//       fd.append('target_level', target_level);
//       fd.append('force_update', String(!!force_update));
//       fd.append('target_ids', JSON.stringify(target_ids || []));
//       return upload('POST', '/ota', fd);
//     },
//   },
//   profile: {
//     updateName: (name) => request('PATCH', '/auth/profile/name', { name }),
//     changePassword: (body) => request('PATCH', '/auth/profile/password', body),
//   },
//   users: {
//     list:   ()           => request('GET',    '/users'),
//     create: (body)        => request('POST',   '/users', body),
//     update: (id, body)    => request('PATCH',  `/users/${id}`, body),
//     delete: (id)          => request('DELETE', `/users/${id}`),
//   },
// };

// export function handleAuthFailure(err, onLogout) {
//   if (err?.status === 401 || err?.message?.includes('Session expired')) {
//     auth.clear();
//     if (typeof onLogout === 'function') onLogout();
//   }
// }